import moment = require("moment");
import React = require("react");
import DatePicker from "react-datepicker";
import {I18n} from "../components/Utils/i18n";
import {AppTranslations} from "../components/Utils/translations/AppTranslations";
import "./filters.scss";
import {ThemeProvider} from "@vebox/etui-system";
import {veboxDefaultTheme} from "../index";

declare var AppTranslations: AppTranslations;
declare var LOCALE: string;

interface StateOption {
    value: string;
    text: string;
    isEmpty?: boolean;
}

interface DateOption {
    value: string;
    text: string;
    showDatepicker: boolean;
    getOptionDate: () => Date;
}

interface FilterProps {
    stateTitleSelect: string;
    periodTitleSelect: string;
    customPeriodTitle: string;
    defaultStateOption: string;
    defaultDateOption: string;
    stateOptions: StateOption[];
    dateOptions: DateOption[];
    onChange: (statusCode: string, date: Date) => void;
    alreadyInitialized?: boolean;
}

interface FilterState {
    currentStateOptionValue: string;
    currentDateOptionValue: string;
    currentDate: Date;
}

export class StateFilterWithDatepicker extends React.Component<FilterProps, FilterState> {

    public static telemetrySetupFilter(onChange: (statusCode: string, date: Date) => void) {
        const telemetrySetupTranslations = AppTranslations.filters.statuses.telemetrySetup;
        return (
            <ThemeProvider theme={veboxDefaultTheme}>
                <StateFilterWithDatepicker
                    stateTitleSelect={telemetrySetupTranslations.title}
                    periodTitleSelect={telemetrySetupTranslations.when}
                    customPeriodTitle={telemetrySetupTranslations.from}
                    defaultStateOption={"-1"}
                    defaultDateOption={"1"}
                    stateOptions={[
                        {value: "-1", text: "", isEmpty: true},
                        {value: "completed", text: telemetrySetupTranslations.completed, isEmpty: false},
                        {value: "notcompleted", text: telemetrySetupTranslations.notCompleted, isEmpty: false},
                    ]}
                    dateOptions={StateFilterWithDatepicker.getGenericDateOptions()}
                    onChange={onChange}
                />
            </ThemeProvider>
        );
    }

    public static lastSignOfLifeFilter(currentStatus: string,
                                       currentDateOption: string,
                                       onChange: (statusCode: string, date: Date) => void) {
        const lastSignOfLifeTranslations = AppTranslations.filters.statuses.lastSignOfLife;
        return (
            <StateFilterWithDatepicker
                stateTitleSelect={lastSignOfLifeTranslations.title}
                periodTitleSelect={AppTranslations.filters.statuses.telemetrySetup.when}
                customPeriodTitle={AppTranslations.filters.statuses.telemetrySetup.from}
                defaultStateOption={currentStatus != null ? currentStatus : "-1"}
                defaultDateOption={currentDateOption != null ? currentDateOption : "1"}
                stateOptions={[
                    {value: "-1", text: "", isEmpty: true},
                    {value: "sent", text: lastSignOfLifeTranslations.sent, isEmpty: false},
                    {value: "notSent", text: lastSignOfLifeTranslations.notSent, isEmpty: false},
                ]}
                dateOptions={StateFilterWithDatepicker.getGenericDateOptions()}
                onChange={onChange}
                alreadyInitialized={currentStatus != null && currentDateOption != null}
            />
        );
    }

    private static getGenericDateOptions() {
        const telemetrySetupTranslations = AppTranslations.filters.statuses.telemetrySetup;
        return [
            {
                value: "1",
                text: telemetrySetupTranslations.today,
                showDatepicker: false,
                getOptionDate: (): Date => {
                    return moment().startOf("day").toDate();
                },
            },
            {
                value: "2",
                text: telemetrySetupTranslations.lastWeek,
                showDatepicker: false,
                getOptionDate: () => {
                    return moment().startOf("day").add(-7, "day").toDate();
                },
            },
            {
                value: "3",
                text: telemetrySetupTranslations.lastMonth,
                showDatepicker: false,
                getOptionDate: () => {
                    return moment().startOf("day").add(-30, "day").toDate();
                },
            },
            {
                value: "4",
                text: telemetrySetupTranslations.customDate,
                showDatepicker: true,
                getOptionDate: () => {
                    return moment().startOf("day").toDate();
                },
            },
        ];
    }

    constructor(props: FilterProps) {
        super(props);
        this.onStateChange = this.onStateChange.bind(this);
        this.onDateOptionChange = this.onDateOptionChange.bind(this);
        this.onDateChange = this.onDateChange.bind(this);
        this.state = {
            currentStateOptionValue: this.props.defaultStateOption,
            currentDateOptionValue: this.props.defaultDateOption,
            currentDate: this.getOptionDate(this.props.defaultDateOption),
        };
    }

    public componentDidMount(): void {
        if (this.props.alreadyInitialized) {
            this.props.onChange(
                this.state.currentStateOptionValue,
                this.state.currentDate,
            );
        }
    }

    public reset() {
        this.setState({
            currentStateOptionValue: this.props.defaultStateOption,
            currentDateOptionValue: this.props.defaultDateOption,
            currentDate: this.getOptionDate(this.props.defaultDateOption),
        });
        this.props.onChange(null, null);
    }

    public render() {
        if (this.props.stateOptions == null || this.props.stateOptions.length === 0) {
            return null;
        }
        return (
            <div className={"state-filter-with-datepicker"}>
                {this.renderStateSelect()}
                {this.renderDateOptionSelect()}
                {this.renderDatepicker()}
            </div>
        );
    }

    private onStateChange(e: React.ChangeEvent<HTMLSelectElement>): void {
        const option = this.findStateOption(e.target.value);
        this.setState({currentStateOptionValue: option.value});
        this.props.onChange(
            option.isEmpty ? null : e.target.value,
            this.state.currentDate,
        );
    }

    private onDateOptionChange(e: React.ChangeEvent<HTMLSelectElement>): void {
        const option = this.findDateOption(e.target.value);
        const date = option.getOptionDate();
        this.setState({currentDate: date, currentDateOptionValue: option.value});
        this.props.onChange(
            this.state.currentStateOptionValue,
            date,
        );
    }

    private onDateChange(date: Date) {
        this.setState({currentDate: date});
        this.props.onChange(
            this.state.currentStateOptionValue,
            date,
        );
    }

    private renderStateSelect() {
        const options: JSX.Element[] = [];
        for (let i = 0; i < this.props.stateOptions.length; i++) {
            options.push(
                (
                    <option value={this.props.stateOptions[i].value} key={i}>
                        {this.props.stateOptions[i].text}
                    </option>
                ),
            );
        }
        let selected = this.state.currentStateOptionValue;
        if (selected == null) {
            selected = this.props.defaultStateOption;
        }
        let classNames = "filterTitleMaterial";
        const option = this.findStateOption(this.state.currentStateOptionValue);
        if (!option.isEmpty) {
            classNames += " filtersGroupTitleEnlight";
        }
        return (
            <React.Fragment>
                <p className={classNames}>{this.props.stateTitleSelect}</p>
                <select className="groupFilterSelect" onChange={this.onStateChange} value={selected}>
                    {options}
                </select>
            </React.Fragment>
        );
    }

    private renderDateOptionSelect() {
        const stateOption = this.findStateOption(this.state.currentStateOptionValue);
        if (stateOption == null || stateOption.isEmpty) {
            return null;
        }
        const options: JSX.Element[] = [];
        for (let i = 0; i < this.props.dateOptions.length; i++) {
            options.push(
                (
                    <option value={this.props.dateOptions[i].value} key={i}>
                        {this.props.dateOptions[i].text}
                    </option>
                ),
            );
        }
        let selected = this.state.currentDateOptionValue;
        if (selected == null) {
            selected = this.props.defaultDateOption;
        }
        return (
            <React.Fragment>
                <p className={"filterTitleMaterial"}>{this.props.periodTitleSelect}</p>
                <select className="groupFilterSelect" onChange={this.onDateOptionChange} value={selected}>
                    {options}
                </select>
            </React.Fragment>
        );
    }

    private renderDatepicker() {
        const dateOption = this.findDateOption(this.state.currentDateOptionValue);
        const stateOption = this.findStateOption(this.state.currentStateOptionValue);
        if (stateOption.isEmpty || dateOption == null || !dateOption.showDatepicker) {
            return null;
        }
        return (
            <React.Fragment>
                <p className={"filterTitleMaterial"}>{this.props.customPeriodTitle}</p>
                <div className={"filterContent filterContentDatePicker"} id={"last-sign-custom-from-date-picker"}>
                    <DatePicker
                        selected={this.state.currentDate}
                        maxDate={new Date()}
                        onChange={this.onDateChange}
                        dateFormat={I18n.getLocaleDateFormat(LOCALE)}
                        // locale={LOCALE}
                        withPortal={false}
                    />
                </div>
            </React.Fragment>
        );
    }

    private getOptionDate(value: string): Date {
        const option = this.findDateOption(value);
        if (option != null && option.getOptionDate) {
            return option.getOptionDate();
        }
        return null;
    }

    private findDateOption(value: string): DateOption {
        if (value === null || this.props.dateOptions === null || this.props.dateOptions.length === 0) {
            return null;
        }
        for (const dateOption of this.props.dateOptions) {
            if (dateOption.value === value) {
                return dateOption;
            }
        }
        return null;
    }

    private findStateOption(value: string): StateOption {
        if (value === null || this.props.stateOptions === null || this.props.stateOptions.length === 0) {
            return null;
        }
        for (const option of this.props.stateOptions) {
            if (option.value === value) {
                return option;
            }
        }
        return null;
    }
}
